import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import { AiOutlineInfo } from "react-icons/ai";
import { PropagateLoader } from "react-spinners";
import Button from "../../../atoms/Button";
import CardHeader from "../../../atoms/CardHeader";
import InputField from "../../../atoms/InputField";
import Modal from "../../../molecules/Modal";
import Breadcrumb from "../../../molecules/Breadcrumb";
import InfoEmpresa from "../InfoEmpresa";
import useModal from "../../../../hooks/useModal";
import { UPLOAD_FILE } from "../../../../api/apiFiles";
import { GET_CASOS_BY_ID } from "../../../../api/apiCasos";
import { GET_ENTERPRISE_BY_ID } from "../../../../api/apiEmpresas";
import { GET_FICHA_TECNICA_BY_CASO, UPDATE_FICHA_TECNICA } from "../../../../api/apiFichaTecnica";
import JsonFileInput from "../../../atoms/JsonFileInput";
import { useSession } from "../../../../context/SessionContext";

const JSON_FIELDS = [
  "review_material",
  "startup_letter",
  "conclusion_review",
  "determining_resolution",
  "tax_credit",
  "determinative_notification",
  "comments_review",
  "comments_determinant",
];

const FCTemplate = () => {
  const { id: enterpriseId, caso_id, etapa } = useParams();
  const { open: openInfo, close, isOpen } = useModal();
  const [caso, setCaso] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEtapa, setLoadingEtapa] = useState(false);
  const { user } = useSession();

  const getCasos = useCallback(async () => {
    try {
      setLoading(true);
      const casoData = await GET_CASOS_BY_ID(caso_id);
      setCaso(casoData ?? null);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo obtener el caso.");
    } finally {
      setLoading(false);
    }
  }, [caso_id]);

  const getFC = useCallback(async () => {
    try {
      setLoadingEtapa(true);
      const fcData = await GET_FICHA_TECNICA_BY_CASO(caso_id);
      setData(fcData ?? null);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo obtener la ficha técnica.");
    } finally {
      setLoadingEtapa(false);
    }
  }, [caso_id]);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const empresaData = await GET_ENTERPRISE_BY_ID(enterpriseId);
        setEmpresa(empresaData ?? null);
      } catch (error) {
        console.error(error);
        toast.error("No se pudo obtener la empresa.");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [enterpriseId]);

  useEffect(() => {
    getCasos();
  }, [getCasos]);

  useEffect(() => {
    getFC();
  }, [getFC]);

  const initialValues = {
    taxpayer: data?.taxpayer || "",
    reviewing_authority: data?.reviewing_authority || "",
    review_material: data?.review_material || null,
    subject_review: data?.subject_review || "",
    start_faculties: data?.start_faculties || "",
    startup_letter: data?.startup_letter || null,
    conclusion_review: data?.conclusion_review || null,
    completion_date: data?.completion_date || "",
    determining_resolution: data?.determining_resolution || null,
    tax_credit: data?.tax_credit || null,
    determinative_date: data?.determinative_date || "",
    determinative_notification: data?.determinative_notification || null,
    comments_review: data?.comments_review || null,
    comments_determinant: data?.comments_determinant || null,
  };

  const validate = () => {
    const errors = {};
    return errors;
  };

  const uploadFileToS3 = async ({ enterpriseId, file, folder }) => {
    const filename = file.name;
    const mimetype = file.type || "application/octet-stream";

    const { url, s3Key } = await UPLOAD_FILE(enterpriseId, filename, mimetype, folder);

    await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": mimetype },
      body: file,
    });

    return { s3Key, filename, mimetype, size: file.size };
  };

  const buildFileRefJson = async (fileInfo, file) => {
    let parsed = null;
    const isJson =
      file?.type === "application/json" ||
      file?.name?.toLowerCase().endsWith(".json");
    if (isJson) {
      try {
        const text = await file.text();
        parsed = JSON.parse(text);
      } catch {
      }
    }
    return {
      filenames: [fileInfo.filename],
      s3Keys: [fileInfo.s3Key],
      mimetype: fileInfo.mimetype,
      size: fileInfo.size,
      parsed,
    };
  };

  const handleSubmit = async (values, { setSubmitting, setFieldValue }) => {
    try {
      const payload = { ...values };

      for (const field of JSON_FIELDS) {
        const val = values?.[field];

        if (val && typeof val === "object" && val._file instanceof File) {
          const file = val._file;
          const folder = `defensa_fiscal/ficha_tecnica/${caso_id}/${field}/`;
          const info = await uploadFileToS3({ enterpriseId, file, folder });
          const jsonRef = await buildFileRefJson(info, file);
          payload[field] = jsonRef;

          await setFieldValue(field, jsonRef, false);
        }
      }

      await UPDATE_FICHA_TECNICA(data?.id, payload);
      toast.success("Ficha técnica actualizada.");
      await getFC();
    } catch (e) {
      console.error(e);
      toast.error("No se pudo actualizar la ficha técnica.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || loadingEtapa) {
    return (
      <div className="h-screen w-full flex justify-center items-center"> <PropagateLoader color="red" /> </div>
    );
  }

  const disabledUser = user && user?.role !== "Client" ? false : true;

  return (
    <div>
      <CardHeader>
        <Breadcrumb
          items={[
            { label: `${empresa?.name || "cargando..."}`, to: `/clientes/empresas/${empresa?.id}/decide`,},
            { label: "DEFENSA FISCAL", to: `/clientes/empresas/${empresa?.id}/defensa_fiscal`,},
            { label: `${caso?.name || "...cargando"}`, to: `/clientes/empresas/${empresa?.id}/defensa_fiscal/${caso?.id}`,},
            { label: "Ficha tecnica", to: `/clientes/empresas/${empresa?.id}/defensa_fiscal/${caso?.id}/${etapa || ""}`,},
          ]}
        />
        <div className="flex flex-row gap-2">
          <Button
            text={<AiOutlineInfo size={22} />}
            bgColor="black"
            width="w-10"
            onClick={openInfo}
          />
        </div>
        {isOpen && empresa && (
          <Modal title="Información" onClose={close}>
            <InfoEmpresa empresa={empresa} />
          </Modal>
        )}
      </CardHeader>

      <div className="p-4">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validate={validate}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, handleChange, handleBlur, values, setFieldValue }) => (
            <Form className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Contribuyente" name="taxpayer" value={values.taxpayer} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <InputField label="Autoridad revisora" name="reviewing_authority" value={values.reviewing_authority} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <InputField label="Materia de revisión" name="subject_review" value={values.subject_review} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <InputField label="Inicio de facultades" type="date" name="start_faculties" value={values.start_faculties} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <InputField label="Fecha de conclusión" type="date" name="completion_date" value={values.completion_date} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <InputField label="Fecha determinativa" type="date" name="determinative_date" value={values.determinative_date} onChange={handleChange} onBlur={handleBlur}  disabled={disabledUser} />
              <JsonFileInput name="review_material" label="Material de revisión" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="startup_letter" label="Oficio de inicio" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="conclusion_review" label="Conclusión de revisión" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="determining_resolution" label="Resolución determinante" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="tax_credit" label="Crédito fiscal" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="determinative_notification" label="Notificación determinante" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="comments_review" label="Comentarios revisión" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <JsonFileInput name="comments_determinant" label="Comentarios determinante" values={values} setFieldValue={setFieldValue} disabled={disabledUser} />
              <div className="md:col-span-2 flex gap-3 pt-2">
                <Button type="submit" bgColor="blue" text={isSubmitting ? "Guardando..." : "Guardar"} disabled={isSubmitting || user?.role === "Client"} />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default FCTemplate;
